import React, { useEffect, useState } from "react";
import { Container, ContentContainer } from "../../common/components/WebLayoutComponents";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import USPSLogo from "../../static/img/LOGO-USPS.png";
import { Api } from "../../services/Api";
import { ButtonContainer, InputContainer } from "./SharedReservationComponents";
import { Modal } from "../../common/components/Modal";
import { useNavigate } from "react-router-dom";
import { LOCKER_LOCATION_PAGE_URL } from "../../config/pages";

function GuestReserveCode() {
   const api = new Api();
   api.setup();

   const navigate = useNavigate();

   const [submitLoading, setSubmitLoading] = useState(false);
   const [invalidCode, setInvalidCode] = useState(false);
   const [emailResendSuccess, setEmailResendSuccess] = useState(false);
   const [emailResendFailed, setEmailResendFailed] = useState(false);
   const [emailResendLoading, setEmailResendLoading] = useState(false);
   const [resendEmailWait, setResendEmailWait] = useState(false);
   const [resendEmailWaitMessage, setResendEmailWaitMessage] = useState(false);
   const [submitError, setSubmitError] = useState(false);

   useEffect(() => {
      if (!sessionStorage.getItem("selectedReservationLocker")) navigate(LOCKER_LOCATION_PAGE_URL);
   }, [navigate]);

   const handleGoBackClick = e => {
      e.preventDefault();

      navigate(-1);
   };

   const formSchema = z.object({
      code: z
         .string()
         .trim()
         .min(1, { message: "Verification code is required." })
         .length(6, { message: "Invalid Verification Code" })
         .regex(/^\d*$/, { message: "The verification code only consists of numbers." }),
   });

   const {
      register,
      handleSubmit,
      formState: { errors, isValid },
   } = useForm({
      mode: "all",
      resolver: zodResolver(formSchema),
   });

   const guestReserveLockerLocation = async verifyCode => {
      return await api.guestReserveLockerLocation(verifyCode);
   };

   const getGuestVerificationCode = async () => {
      return await api.getGuestVerificationCode();
   };

   const handleResendCodeClick = async () => {
      setResendEmailWaitMessage(false);
      setEmailResendLoading(true);
      setEmailResendSuccess(false);
      setEmailResendFailed(false);
      if (resendEmailWait) {
         setResendEmailWaitMessage(true);
      } else {
         getGuestVerificationCode()
            .then(resp => {
               setEmailResendLoading(false);
               if (resp.ok) {
                  console.log("Valid Resp:", resp);
                  setEmailResendSuccess(true);
                  setResendEmailWait(true);
                  setTimeout(() => setResendEmailWait(false), 60000);
               } else {
                  console.log("Error:", resp);
                  setSubmitError(true);
                  setEmailResendFailed(true);
               }
            })
            .catch(err => {
               setSubmitError(true);
               setEmailResendFailed(true);
               console.log("error:", err);
            });
      }
   };

   const onSubmit = data => {
      setSubmitLoading(true);
      if (isValid) {
         guestReserveLockerLocation(data?.code)
            .then(resp => {
               if (resp.ok) {
                  if (resp.data !== "Validation Failed") {
                     window.sessionStorage.setItem("reserve-locker-code", resp.data);
                     navigate("/guest-reservation/success");
                  } else {
                     setInvalidCode(true);
                  }
               } else {
                  console.log("Then Block Error:", resp);
               }
            })
            .catch(err => {
               console.log("Catch Error:", err);
            });
      }
      setSubmitLoading(false);
   };

   return (
      <Container>
         <Modal modalActive={submitError} closeFunction={() => setSubmitError(false)}>
            <h1>Hmm...</h1>
            <p>Looks like something went wrong, please try again later.</p>
            <button onClick={() => setSubmitError(false)}>Close</button>
         </Modal>
         <ContentContainer
            className="full reserve-width"
            style={{ marginTop: "40px", marginBottom: "40px", color: "black", maxWidth: "1300px" }}
         >
            <img src={USPSLogo} alt="USPS Logo" style={{ marginBottom: "60px" }} />
            <h1>Enter verification code</h1>
            <p style={{ maxWidth: "510px" }}>
               We've sent a six-digit verification code to your email ({sessionStorage.getItem("guest_reserve_email")}).
               To continue, please enter this code below:
            </p>
            <form id="verification-code-form" onSubmit={handleSubmit(onSubmit)}>
               <InputContainer style={{ marginBottom: "10px" }}>
                  <label htmlFor="verification-code">Verification Code</label>
                  <input
                     type="text"
                     {...register("code")}
                     className={(invalidCode || errors.code?.message) && "red-border-error"}
                  />
                  {!errors.code?.message && invalidCode && <div style={{ color: "red" }}>Invalid Code</div>}
                  <div style={{ color: "red" }}>{errors.code?.message}</div>
                  <div>
                     <p style={{ marginTop: "13px" }}>
                        <strong>Didn't receive a code? </strong>
                        Please allow a few minutes for the message to arrive or check your spam or junk folder.
                        Otherwise, you can request to{" "}
                        <span
                           className="resend-code"
                           style={{ width: "fit-content", marginBottom: "0", marginRight: "0" }}
                           onClick={handleResendCodeClick}
                           onKeyDown={handleResendCodeClick}
                           disabled={emailResendLoading}
                        >
                           resend the code
                        </span>
                        .
                     </p>
                     {emailResendSuccess && <span style={{ color: "green" }}>Code Resent!</span>}
                     {emailResendFailed && <span style={{ color: "red" }}>Code failed to resend.</span>}
                     {resendEmailWaitMessage && (
                        <span style={{ color: "red" }}>
                           Code has already been resent! Please wait a minute in between requesting emails.
                        </span>
                     )}
                  </div>
               </InputContainer>
               <ButtonContainer style={{ marginTop: "20px" }}>
                  <input
                     type="submit"
                     form="verification-code-form"
                     value={submitLoading ? "Verifying..." : "Verify"}
                     className="submit-button-active"
                     disabled={submitLoading}
                     style={{ marginRight: "30px" }}
                  />
                  <button className="usps-secondary-button" onClick={handleGoBackClick}>
                     Go Back
                  </button>
               </ButtonContainer>
            </form>
         </ContentContainer>
      </Container>
   );
}

export default GuestReserveCode;
